import React, { createContext, useContext, useMemo, useState } from "react";

type TKeyHandler<TValue> = {
  read: () => TValue;
  write: (value: TValue) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TStorageHandlers = Record<string, TKeyHandler<any>>;

type TContextValue<THandlers extends TStorageHandlers> = {
  [K in keyof THandlers]: {
    value: ReturnType<THandlers[K]["read"]>;
    setValue: (value: ReturnType<THandlers[K]["read"]>) => void;
  };
};

/**
 * Creates a React context provider for localStorage-backed state
 *
 * @description
 * - wraps localStorage key handlers with React state management
 * - each key gets reactive state that persists to localStorage on write
 * - useful for persisted UI preferences that need to be shared across distant components
 * - avoids prop drilling while keeping state scoped to specific component trees
 *
 * @example
 * ```tsx
 * const AdminLocalStorage = useAdminLocalStorage();
 *
 * const { Provider: AdminStorageProvider, useStorage: useAdminStorageContext } =
 *   createLocalStorageContextProvider(AdminLocalStorage);
 *
 * // Wrap your component tree
 * <AdminStorageProvider>
 *   <App />
 * </AdminStorageProvider>
 *
 * // Use in any child component
 * function MyComponent() {
 *   const { deleteColumnVisibility } = useAdminStorageContext();
 *
 *   return (
 *     <Toggle
 *       checked={deleteColumnVisibility.value}
 *       onChange={() => deleteColumnVisibility.setValue(!deleteColumnVisibility.value)}
 *     />
 *   );
 * }
 * ```
 */
export default function createLocalStorageContextProvider<
  THandlers extends TStorageHandlers,
>(handlers: THandlers) {
  type TContext = TContextValue<THandlers>;

  const Context = createContext<TContext | null>(null);

  function Provider({ children }: { children: React.ReactNode }) {
    // Create state hooks for each handler - using reduce to avoid for...of
    const stateEntries = Object.entries(handlers).reduce<
      Record<string, { value: unknown; setValue: (v: unknown) => void }>
    >((acc, [key, handler]) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return
      const [value, setValue] = useState(() => handler.read());

      const setValueAndPersist = (newValue: unknown) => {
        setValue(newValue);
        handler.write(newValue);
      };

      return {
        ...acc,
        [key]: { value, setValue: setValueAndPersist },
      };
    }, {});

    const contextValue = useMemo(
      () => stateEntries as TContext,
      [stateEntries],
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  }

  function useStorage(): TContext {
    const context = useContext(Context);

    if (!context) {
      throw new Error(
        "useStorage must be used within a LocalStorageContextProvider",
      );
    }

    return context;
  }

  return {
    Provider,
    useStorage,
  };
}
